import { useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import * as Sentry from '@sentry/tanstackstart-react'
import { useCurrentUser } from '../hooks/useCurrentUser'
import { useMixpanelIdentify } from '../hooks/useMixpanelIdentify'
import { SubscriptionProvider } from '../context/SubscriptionContext'
import { ActiveJobsProvider } from '../context/ActiveJobsContext'
import SubscriptionBanner from './SubscriptionBanner'
import TrialBanner from './TrialBanner'
import ActiveJobIndicator from './ActiveJobIndicator'

interface AuthGuardProps {
  children: React.ReactNode
}

export default function AuthGuard({ children }: AuthGuardProps) {
  const navigate = useNavigate()
  const { data: user, isLoading } = useCurrentUser()

  useMixpanelIdentify(user)

  useEffect(() => {
    if (!isLoading && user) {
      Sentry.setUser({
        id: user.id,
        role: user.role,
        familyId: user.familyId ?? undefined,
        ...(user.tenantType === 'school' ? { schoolId: user.tenantId } : {}),
      })
    } else if (!isLoading && !user) {
      Sentry.setUser(null)
    }
  }, [isLoading, user])

  useEffect(() => {
    if (!isLoading && !user) {
      navigate({ to: '/login', search: { redirect: window.location.pathname } })
    }
    // School staff should be on the school dashboard, not the family dashboard
    if (!isLoading && user?.tenantType === 'school' && user.tenantId) {
      const schoolPath = `/school/${user.tenantId}`
      if (!window.location.pathname.startsWith(schoolPath) && !window.location.pathname.startsWith('/admin')) {
        navigate({ to: '/school/$schoolId', params: { schoolId: user.tenantId } })
      }
    }
  }, [isLoading, user, navigate])

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" role="status">
        <div className="h-8 w-8 motion-safe:animate-spin rounded-full border-b-2 border-brand" />
        <span className="sr-only">Loading</span>
      </div>
    )
  }

  if (!user) {
    return null
  }

  // Block rendering of family content for school staff — redirect is in-flight via useEffect above
  if (user.tenantType === 'school' && user.tenantId) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" role="status">
        <div className="h-8 w-8 motion-safe:animate-spin rounded-full border-b-2 border-brand" />
        <span className="sr-only">Loading</span>
      </div>
    )
  }

  return (
    <SubscriptionProvider userRole={user.role}>
      <ActiveJobsProvider>
        {children}
        <SubscriptionBanner />
        <TrialBanner />
        <ActiveJobIndicator />
      </ActiveJobsProvider>
    </SubscriptionProvider>
  )
}
